import { useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import {
  endpoints,
  numericToNumber,
  APIError,
  type CampaignChannelByChannelItem,
  type Channel,
  type ChannelSnapshot,
  type Evaluation,
  type UpdateChannelRequest,
  type Video,
} from "@/lib/api";
import {
  fmtDate,
  fmtNumber,
  fmtPercent,
  fmtRank,
  fmtTimestamp,
} from "@/lib/format";
import { PlatformBadge } from "@/components/PlatformBadge";
import { Badge, decisionVariant, lifecycleVariant, lifecycleLabel } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { CompetitorOverlapCell } from "@/components/CompetitorOverlapCell";
import { Input } from "@/components/ui/Input";
import { Lbl } from "@/components/ui/Lbl";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { ChannelSponsorshipsSection } from "@/components/channel/ChannelSponsorshipsSection";
import { ChannelCRMSection } from "@/components/channel/ChannelCRMSection";
import { Field } from "@/components/ui/Field";
import { EntityPicker } from "@/components/EntityPicker";

// ---------------------------------------------------------------------------
// Channel detail deep page — accessed from the Channels table drawer or via
// direct URL /channels/:id. Surfaces the full picture for one channel: meta,
// score snapshot, follower-count time series, recent videos, decision log.
// ---------------------------------------------------------------------------

const RANGE_OPTIONS = [
  { value: 30, label: "30 天" },
  { value: 90, label: "90 天" },
  { value: 180, label: "180 天" },
  { value: 365, label: "365 天" },
];

export default function ChannelDetail() {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const id = Number(params.id);
  const [range, setRange] = useState(90);
  const [editing, setEditing] = useState(false);
  const [movingEntity, setMovingEntity] = useState(false);
  const qc = useQueryClient();

  const channelQ = useQuery({
    queryKey: ["channel", id],
    queryFn: () => endpoints.channel(id),
    enabled: Number.isFinite(id),
  });
  const scoreQ = useQuery({
    queryKey: ["channel-score", id],
    queryFn: () => endpoints.channelScore(id),
    enabled: Number.isFinite(id),
    retry: (n, err) => !(err instanceof APIError && err.status === 404) && n < 2,
  });
  const snapsQ = useQuery({
    queryKey: ["channel-snapshots", id, range],
    queryFn: () => endpoints.channelSnapshots(id, range),
    enabled: Number.isFinite(id),
  });
  // v2.3.4 — bumped 15→50 (videos) and 20→50 (evaluations). Current scale
  // (avg 24 videos/channel, < 1 evaluation/channel) means 50 covers virtually
  // every channel without paginating. Real prev/next can be added when a
  // single channel exceeds 50, which isn't close yet.
  const videosQ = useQuery({
    queryKey: ["channel-videos", id, 50],
    queryFn: () => endpoints.channelVideos(id, 50),
    enabled: Number.isFinite(id),
  });
  const evalsQ = useQuery({
    queryKey: ["channel-evals", id, 50],
    queryFn: () => endpoints.channelEvaluations(id, 50),
    enabled: Number.isFinite(id),
  });
  const campaignsQ = useQuery({
    queryKey: ["channel-campaigns", id],
    queryFn: () => endpoints.campaignsByChannel(id),
    enabled: Number.isFinite(id),
  });
  // v2.8.0 Day 6 — surface entity attribution + sibling channels in the
  // header area. enabled gates on EntityID present so we don't 404 the
  // (rare) orphan-NULL row.
  const channelEntityID = channelQ.data?.EntityID;
  const entityQ = useQuery({
    queryKey: ["entities", "detail", channelEntityID],
    queryFn: () => endpoints.entity(channelEntityID!),
    enabled: !!channelEntityID,
    refetchOnWindowFocus: false,
  });

  const moveMut = useMutation({
    mutationFn: ({ toEntityID, reason }: { toEntityID: number; reason: string }) =>
      endpoints.moveChannelToEntity(id, toEntityID, { reason: reason || undefined }),
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ["channel", id] });
      if (channelEntityID) {
        qc.invalidateQueries({ queryKey: ["entities", "detail", channelEntityID] });
      }
      qc.invalidateQueries({ queryKey: ["entities", "detail", resp.to_entity_id] });
      qc.invalidateQueries({ queryKey: ["entities", "list"] });
      // v2.8.0 Day 7 — also invalidate the EntitySelf cache so moves
      // INTO the Bitbase-self entity propagate without manual reload.
      qc.invalidateQueries({ queryKey: ["entities", "self"] });
    },
  });

  if (!Number.isFinite(id)) {
    return <div className="p-6 text-sm text-destructive">无效的频道 ID</div>;
  }

  const channel = channelQ.data;
  const score = scoreQ.data;

  return (
    <div className="h-full overflow-y-auto bg-muted/30">
      <div className="border-b border-border bg-background px-6 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            ← 返回
          </Button>
          <h1 className="text-lg font-semibold">
            {channel?.Name ?? channel?.Handle ?? `Channel #${id}`}
            {channel?.Name && channel?.Handle && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                {channel.Handle}
              </span>
            )}
          </h1>
          {channel && (
            <Badge variant={lifecycleVariant(channel.LifecycleStatus)}>
              {lifecycleLabel(channel.LifecycleStatus)}
            </Badge>
          )}
          <div className="ml-auto flex items-center gap-2">
            {channel && <LifecycleControls channel={channel} />}
            {channel && (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                编辑
              </Button>
            )}
            <Link
              to="/channels"
              className="text-xs text-muted-foreground hover:text-foreground hover:underline"
            >
              ← 频道库
            </Link>
          </div>
        </div>
      </div>

      {editing && channel && (
        <EditChannelModal
          channel={channel}
          onClose={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      )}

      <div className="mx-auto max-w-5xl space-y-6 p-6">
        {channelQ.isLoading && <p className="text-sm text-muted-foreground">加载中…</p>}
        {channelQ.isError && (
          <p className="text-sm text-destructive">
            加载失败：{(channelQ.error as Error).message}
          </p>
        )}

        {channel && (
          <section className="rounded-md border border-border bg-background p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                  <PlatformBadge platform={channel.Platform} />
                  <span>· {channel.PlatformChannelID}</span>
                </p>
                {channel.Url && (
                  <a
                    href={channel.Url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-primary hover:underline break-all"
                  >
                    {channel.Url}
                  </a>
                )}
                {channel.Description && (
                  <p className="mt-2 line-clamp-3 max-w-prose text-sm text-muted-foreground">
                    {channel.Description}
                  </p>
                )}
              </div>
            </div>
            <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm md:grid-cols-4">
              <Field label="粉丝">{fmtNumber(channel.FollowerCount)}</Field>
              <Field label="内容数">{fmtNumber(channel.TotalContentCount)}</Field>
              <Field label="累计观看">{fmtNumber(channel.TotalViewCount)}</Field>
              <Field label="跟踪模式">{channel.TrackingMode}</Field>
              <Field label="内容类型">{channel.ContentType ?? "—"}</Field>
              <Field label="区域">{channel.Region ?? "—"}</Field>
              <Field label="语言">{channel.Language ?? "—"}</Field>
              <Field label="内部分级">{channel.InternalTier ?? "—"}</Field>
              <Field label="最后内容">{fmtTimestamp(channel.LastContentAt)}</Field>
              <Field label="最近抓取">{fmtTimestamp(channel.LastFetchedAt)}</Field>
              <Field label="下次复评">{fmtTimestamp(channel.NextEvaluationAt)}</Field>
              <Field label="发现来源">{channel.DiscoveredVia ?? "—"}</Field>
            </dl>
          </section>
        )}

        {channel && (
          <section className="rounded-md border border-border bg-background p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold">归属 entity</h2>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setMovingEntity(true)}
              >
                移动到 entity…
              </Button>
            </div>
            {entityQ.isLoading && (
              <p className="text-xs text-muted-foreground">加载中…</p>
            )}
            {entityQ.data && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Link
                    to={`/entities/${entityQ.data.id}`}
                    className="text-sm font-medium text-primary hover:underline"
                  >
                    {entityQ.data.canonical_name}
                  </Link>
                  {entityQ.data.is_internal && (
                    <Badge className="bg-amber-100 text-amber-700">★ 我们</Badge>
                  )}
                  {entityQ.data.primary_handle && (
                    <span className="font-mono text-xs text-muted-foreground">
                      {entityQ.data.primary_handle}
                    </span>
                  )}
                  <span className="ml-auto text-xs text-muted-foreground">
                    共 {entityQ.data.channels.length} 个 channel
                  </span>
                </div>
                {entityQ.data.channels.length > 1 && (
                  <div>
                    <p className="text-xs text-muted-foreground">同 entity 下的其他频道:</p>
                    <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs">
                      {entityQ.data.channels
                        .filter((c) => c.id !== id)
                        .map((c) => (
                          <li key={c.id}>
                            <Link
                              to={`/channels/${c.id}`}
                              className="text-primary hover:underline"
                            >
                              [{c.platform}] {c.handle ?? c.name ?? `#${c.id}`}
                            </Link>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {channel && (
          <EntityPicker
            open={movingEntity}
            channelLabel={channel.Handle ?? channel.Name ?? `#${channel.ID}`}
            excludeEntityID={channel.EntityID}
            onClose={() => setMovingEntity(false)}
            onPick={async (toEntityID, reason) => {
              await moveMut.mutateAsync({ toEntityID, reason });
              setMovingEntity(false);
            }}
          />
        )}

        <section className="rounded-md border border-border bg-background p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold">粉丝时间序列</h2>
            <Select
              value={range}
              onChange={(e) => setRange(Number(e.target.value))}
              className="w-32"
            >
              {RANGE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </Select>
          </div>
          <FollowerChart snapshots={snapsQ.data ?? []} loading={snapsQ.isLoading} />
        </section>

        <section className="rounded-md border border-border bg-background p-4">
          <h2 className="mb-3 text-sm font-semibold">评分快照</h2>
          {scoreQ.isLoading && <p className="text-xs text-muted-foreground">加载中…</p>}
          {!scoreQ.isLoading && !score && (
            <p className="text-xs text-muted-foreground">
              尚未计算分数（compute_scores 任务还没跑到这个频道）
            </p>
          )}
          {score && (
            <>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                <ScoreCell label="粉丝" value={fmtNumber(score.FollowerCount)} rank={fmtRank(numericToNumber(score.FollowerPct))} />
                <ScoreCell label="30 日活跃" value={fmtNumber(score.Activity30d)} rank={fmtRank(numericToNumber(score.ActivityPct))} />
                <ScoreCell label="互动率 (30d)" value={fmtPercent(numericToNumber(score.EngagementRate), 2)} rank={fmtRank(numericToNumber(score.EngagementPct))} />
                <ScoreCell label="90 日增长" value={fmtPercent(numericToNumber(score.GrowthRate90d), 1)} rank={fmtRank(numericToNumber(score.GrowthPct))} />
                <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
                  <div className="text-xs text-muted-foreground">竞品重叠 (90d)</div>
                  <div className="mt-1">
                    <CompetitorOverlapCell channelID={id} overlap={score.CompetitorOverlap} />
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">点击展开</div>
                </div>
              </div>
              <p className="mt-2 text-[10px] text-muted-foreground">
                计算于 {fmtTimestamp(score.ComputedAt)}
                {score.AutoDecisionSuggestion && (
                  <Badge className="ml-2" variant={decisionVariant(score.AutoDecisionSuggestion)}>
                    自动建议：{score.AutoDecisionSuggestion}
                  </Badge>
                )}
              </p>
            </>
          )}
        </section>

        <section className="rounded-md border border-border bg-background p-4">
          <h2 className="mb-3 text-sm font-semibold">
            {channel?.Platform === "twitter"
              ? "最近推文"
              : channel?.Platform === "youtube"
              ? "最近视频"
              : channel?.Platform === "tiktok"
              ? "最近短视频"
              : "最近内容"}
            {videosQ.data && (
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                ({videosQ.data.data.length} 条)
              </span>
            )}
          </h2>
          <RecentVideos videos={videosQ.data?.data ?? []} loading={videosQ.isLoading} />
        </section>

        {channel && (
          <section className="rounded-md border border-border bg-background p-4">
            <ChannelCRMSection channel={channel} />
          </section>
        )}

        <section className="rounded-md border border-border bg-background p-4">
          <h2 className="mb-3 text-sm font-semibold">投放</h2>
          <CampaignList items={campaignsQ.data ?? []} loading={campaignsQ.isLoading} />
        </section>

        <section
          id="competitor-sponsorships"
          className="scroll-mt-16 rounded-md border border-border bg-background p-4"
        >
          <ChannelSponsorshipsSection channelID={id} />
        </section>

        <section className="rounded-md border border-border bg-background p-4">
          <h2 className="mb-3 text-sm font-semibold">决策历史</h2>
          <DecisionLog evals={evalsQ.data?.data ?? []} loading={evalsQ.isLoading} />
        </section>
      </div>
    </div>
  );
}

// LifecycleControls — operator-facing state machine override. Three actions:
//
//   1. 改状态  — set lifecycle to any allowed value. Use when the auto
//      pipeline got it wrong (e.g. accidentally rejected, want to flip to
//      candidate before signing a deal).
//   2. 重新评估 — create a new evaluation_task. The channel re-enters the
//      evaluation hub regardless of current lifecycle.
//   3. 归档     — soft delete. Equivalent to setting lifecycle='archived',
//      but more discoverable as a named action.
function LifecycleControls({ channel }: { channel: Channel }) {
  const qc = useQueryClient();
  const [showLifecycle, setShowLifecycle] = useState(false);

  const lifecycleMut = useMutation({
    mutationFn: (status: string) =>
      endpoints.updateChannelLifecycle(channel.ID, {
        lifecycle_status: status as
          | "discovered"
          | "evaluating"
          | "candidate"
          | "active"
          | "monitor_only"
          | "rejected"
          | "archived",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["channel", channel.ID] });
      qc.invalidateQueries({ queryKey: ["channels"] });
      setShowLifecycle(false);
    },
  });

  const reevalMut = useMutation({
    mutationFn: () => endpoints.reevaluateChannel(channel.ID),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["evaluation-tasks"] });
      alert("已创建评估任务，去评估中心查看");
    },
    onError: (err) => {
      const msg = err instanceof APIError ? err.message : (err as Error).message;
      alert(`重评失败: ${msg}`);
    },
  });

  return (
    <div className="flex items-center gap-2">
      <Button
        size="sm"
        variant="outline"
        onClick={() => reevalMut.mutate()}
        disabled={reevalMut.isPending}
      >
        重新评估
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setShowLifecycle((v) => !v)}
      >
        改状态 ▾
      </Button>
      {channel.LifecycleStatus !== "archived" && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            if (confirm("确认归档此 KOL？历史数据保留，列表里默认看不到，可勾选'含归档'恢复。")) {
              lifecycleMut.mutate("archived");
            }
          }}
        >
          归档
        </Button>
      )}
      {showLifecycle && (
        <div className="absolute z-10 mt-2 translate-y-8 rounded-md border border-border bg-background p-2 text-xs shadow-md">
          {[
            "discovered",
            "evaluating",
            "candidate",
            "active",
            "monitor_only",
            "rejected",
          ].map((s) => {
            // v2.4.3 — stronger "current" affordance. Was just text "(当前)"
            // which is easy to miss; mis-clicking onto the active row was a
            // common fat-finger. Bold + tinted background makes the row
            // look unmistakably "this is what you already are".
            const isCurrent = s === channel.LifecycleStatus;
            return (
              <button
                key={s}
                className={`block w-full px-3 py-1.5 text-left ${
                  isCurrent
                    ? "bg-primary/10 font-semibold text-primary cursor-default"
                    : "hover:bg-muted"
                }`}
                disabled={isCurrent || lifecycleMut.isPending}
                onClick={() => lifecycleMut.mutate(s)}
              >
                {isCurrent ? `✓ ${lifecycleLabel(s)} (当前)` : lifecycleLabel(s)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ScoreCell({ label, value, rank }: { label: string; value: string; rank: string }) {
  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-base font-semibold">{value}</div>
      <div className="text-xs text-muted-foreground">{rank}</div>
    </div>
  );
}

function FollowerChart({
  snapshots,
  loading,
}: {
  snapshots: ChannelSnapshot[];
  loading: boolean;
}) {
  const data = useMemo(
    () =>
      snapshots
        .filter((s) => s.CapturedAt !== null && s.FollowerCount !== null)
        .map((s) => ({
          ts: s.CapturedAt ?? "",
          followers: s.FollowerCount ?? 0,
        })),
    [snapshots],
  );

  if (loading) {
    return <p className="text-xs text-muted-foreground">加载中…</p>;
  }
  if (data.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        暂无快照数据（channel_followers_refresh 任务尚未填充时间序列）
      </p>
    );
  }

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis
            dataKey="ts"
            tickFormatter={(t) => new Date(t).toLocaleDateString()}
            tick={{ fontSize: 11 }}
          />
          <YAxis
            tickFormatter={(v) => abbrev(v as number)}
            tick={{ fontSize: 11 }}
            width={48}
          />
          <Tooltip
            labelFormatter={(t) => new Date(t as string).toLocaleString()}
            formatter={(v) => [fmtNumber(v as number), "粉丝"]}
          />
          <Line
            type="monotone"
            dataKey="followers"
            stroke="#0ea5e9"
            strokeWidth={2}
            dot={{ r: 2 }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function abbrev(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function RecentVideos({ videos, loading }: { videos: Video[]; loading: boolean }) {
  if (loading) return <p className="text-xs text-muted-foreground">加载中…</p>;
  if (videos.length === 0) return <p className="text-xs text-muted-foreground">暂无</p>;
  return (
    <ul className="space-y-2">
      {videos.map((v) => (
        <li key={v.ID} className="rounded border border-border bg-muted/20 px-3 py-2">
          <div className="text-sm font-medium line-clamp-1">
            {v.Url ? (
              <a href={v.Url} target="_blank" rel="noreferrer" className="hover:underline">
                {v.Title ?? v.PlatformVideoID}
              </a>
            ) : (
              v.Title ?? v.PlatformVideoID
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            {fmtDate(v.PublishedAt)}
            <span className="mx-2">·</span>
            👁 {fmtNumber(v.ViewCount)}
            <span className="mx-2">·</span>
            👍 {fmtNumber(v.LikeCount)}
            <span className="mx-2">·</span>
            💬 {fmtNumber(v.CommentCount)}
          </div>
        </li>
      ))}
    </ul>
  );
}

function CampaignList({ items, loading }: { items: CampaignChannelByChannelItem[]; loading: boolean }) {
  if (loading) return <p className="text-xs text-muted-foreground">加载中…</p>;
  if (items.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        暂无投放记录 —
        <Link to="/campaigns" className="ml-1 text-primary hover:underline">
          去投放管理新建
        </Link>
      </p>
    );
  }
  return (
    <ul className="space-y-2">
      {items.map((c) => (
        <li key={c.campaign_channel_id} className="rounded border border-border bg-muted/20 px-3 py-2">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Link to={`/campaigns/${c.campaign_id}`} className="font-medium text-primary hover:underline">
              {c.campaign_name}
            </Link>
            <Badge variant={c.campaign_status === "active" ? "success" : c.campaign_status === "cancelled" ? "danger" : "secondary"}>
              {c.campaign_status}
            </Badge>
            {c.referral_code && (
              <code className="rounded bg-muted px-1 text-xs">{c.referral_code}</code>
            )}
            {c.kol_uid && (
              <code className="rounded bg-muted px-1 text-xs">UID {c.kol_uid}</code>
            )}
            {c.deal_amount != null && (
              <Badge variant="info">
                {c.payment_currency === "USD" ? "$" : ""}
                {fmtNumber(c.deal_amount)}
                {c.payment_currency && c.payment_currency !== "USD" ? ` ${c.payment_currency}` : ""}
              </Badge>
            )}
            {c.has_deal_amount && c.deal_amount == null && (
              <Badge variant="secondary">金额解密失败</Badge>
            )}
            <span className="ml-auto text-xs text-muted-foreground">
              {c.campaign_start_date ?? "—"} → {c.campaign_end_date ?? "—"}
            </span>
          </div>
          {c.agreed_deliverables && (
            <p className="mt-1 text-xs text-muted-foreground">{c.agreed_deliverables}</p>
          )}
        </li>
      ))}
    </ul>
  );
}

// CompetitorSponsorshipsSection — v2.1.3 — was previously one row per
// (video × exchange) sponsorship which exploded for X tweets that mention
// many exchanges in one shot ("零撸空投" tweet → 6 rows for 6 exchanges).
// Now: top summary card shows exchange frequency, list groups by content
// and renders exchanges as chips inside each content card.

function DecisionLog({ evals, loading }: { evals: Evaluation[]; loading: boolean }) {
  if (loading) return <p className="text-xs text-muted-foreground">加载中…</p>;
  if (evals.length === 0) return <p className="text-xs text-muted-foreground">暂无决策记录</p>;
  return (
    <ul className="space-y-2">
      {evals.map((e) => (
        <li key={e.ID} className="rounded border border-border bg-muted/20 px-3 py-2">
          <div className="flex items-center gap-2 text-sm">
            <Badge variant={decisionVariant(e.Decision)}>{e.Decision}</Badge>
            <span className="text-muted-foreground">{fmtDate(e.EvaluatedAt)}</span>
            <span className="text-xs text-muted-foreground">·</span>
            <span className="text-xs text-muted-foreground">
              方法：{e.DecisionMethod ?? "—"}
            </span>
            {e.Evaluator && (
              <span className="ml-auto text-xs text-muted-foreground">by {e.Evaluator}</span>
            )}
          </div>
          {e.Rationale && (
            <p className="mt-1 text-xs text-muted-foreground">{e.Rationale}</p>
          )}
          <p className="mt-1 text-[10px] text-muted-foreground">
            下次复评：{fmtTimestamp(e.NextReviewAt)}
          </p>
        </li>
      ))}
    </ul>
  );
}

// EditChannelModal lets the operator fill BD/classification fields YouTube
// doesn't provide reliably: content_type / region / language / internal_tier /
// quote info. Each field is initialized from the current channel and
// changes are sent via PATCH /api/channels/{id}.
function EditChannelModal({
  channel,
  onClose,
  onSaved,
}: {
  channel: Channel;
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<{
    content_type: string;
    region: string;
    language: string;
    internal_tier: string;
    quoted_price_usd: string;
    quote_type: string;
    quote_details: string;
    referral_code: string;
    kol_uid: string;
  }>({
    content_type: channel.ContentType ?? "",
    region: channel.Region ?? "",
    language: channel.Language ?? "",
    internal_tier: channel.InternalTier ?? "",
    quoted_price_usd: "",
    quote_type: channel.QuoteType ?? "",
    quote_details: channel.QuoteDetails ?? "",
    referral_code: channel.ReferralCode ?? "",
    kol_uid: channel.KolUid ?? "",
  });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: UpdateChannelRequest) =>
      endpoints.updateChannel(channel.ID, body),
    onSuccess: () => {
      // Refresh channel + dependent queries so the page picks up new values
      // and the scoring engine's next tick sees the updated content_type.
      qc.invalidateQueries({ queryKey: ["channel", channel.ID] });
      qc.invalidateQueries({ queryKey: ["channels"] });
      onSaved();
    },
    onError: (err) => setError((err as Error).message),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    // Empty strings are dropped server-side (no-change). Operators can't
    // explicitly NULL a field in v1; this matches Phase 1 expectations.
    const body: UpdateChannelRequest = {
      content_type: form.content_type || undefined,
      region: form.region || undefined,
      language: form.language || undefined,
      internal_tier: form.internal_tier || undefined,
      quote_type: form.quote_type || undefined,
      quote_details: form.quote_details || undefined,
      referral_code: form.referral_code || undefined,
      kol_uid: form.kol_uid || undefined,
    };
    if (form.quoted_price_usd) {
      const n = Number(form.quoted_price_usd);
      if (Number.isFinite(n) && n > 0) body.quoted_price_usd = n;
    }
    mutation.mutate(body);
  }

  return (
    <Modal
      title={`编辑频道 · ${channel.Name ?? channel.Handle}`}
      onClose={onClose}
      onSubmit={submit}
      error={error}
      submitLabel="保存"
      submittingLabel="保存中…"
      submitting={mutation.isPending}
    >
      <p className="mb-3 text-xs text-muted-foreground">
        YouTube 不返回的运营字段在这里手填。<code>content_type</code>{" "}
        会决定百分位计算分桶。空字段保持原值，不清空。
      </p>

      <div className="grid grid-cols-2 gap-3">
        <Lbl label="内容类型">
          <Select
            value={form.content_type}
            onChange={(e) => setForm({ ...form, content_type: e.target.value })}
          >
            <option value="">— 不设置 —</option>
            <option value="review_analysis">review_analysis (评测/分析)</option>
            <option value="education">education (教程)</option>
            <option value="news_commentary">news_commentary (新闻/评论)</option>
            <option value="trading">trading (交易)</option>
            <option value="tech">tech (技术)</option>
            <option value="lifestyle">lifestyle (生活)</option>
            <option value="other">other</option>
          </Select>
        </Lbl>
        <Lbl label="区域">
          <Input
            value={form.region}
            onChange={(e) => setForm({ ...form, region: e.target.value })}
            placeholder="en / cn-zh / global / sea ..."
          />
        </Lbl>
        <Lbl label="语言">
          <Input
            value={form.language}
            onChange={(e) => setForm({ ...form, language: e.target.value })}
            placeholder="en / zh / es / ..."
          />
        </Lbl>
        <Lbl label="内部分级">
          <Select
            value={form.internal_tier}
            onChange={(e) => setForm({ ...form, internal_tier: e.target.value })}
          >
            <option value="">— 不设置 —</option>
            <option value="T1">T1 (头部)</option>
            <option value="T2">T2 (中部)</option>
            <option value="T3">T3 (尾部)</option>
          </Select>
        </Lbl>
        <Lbl label="报价 USD">
          <Input
            type="number"
            step="0.01"
            value={form.quoted_price_usd}
            onChange={(e) => setForm({ ...form, quoted_price_usd: e.target.value })}
            placeholder="3000"
          />
        </Lbl>
        <Lbl label="报价类型">
          <Select
            value={form.quote_type}
            onChange={(e) => setForm({ ...form, quote_type: e.target.value })}
          >
            <option value="">— 不设置 —</option>
            <option value="flat">flat (一口价)</option>
            <option value="cpm">cpm (按观看)</option>
            <option value="hybrid">hybrid</option>
            <option value="revshare">revshare (返佣)</option>
          </Select>
        </Lbl>
        <Lbl label="报价备注" full>
          <Input
            value={form.quote_details}
            onChange={(e) => setForm({ ...form, quote_details: e.target.value })}
            placeholder="1 主内容 + 2 短内容 + 钉置顶 7 天"
          />
        </Lbl>
        <Lbl label="返佣码 (Bitbase)">
          <Input
            value={form.referral_code}
            onChange={(e) => setForm({ ...form, referral_code: e.target.value })}
            placeholder="ALICEREF"
          />
        </Lbl>
        <Lbl label="UID (Bitbase)">
          <Input
            value={form.kol_uid}
            onChange={(e) => setForm({ ...form, kol_uid: e.target.value })}
            placeholder="1284107203"
          />
        </Lbl>
      </div>
    </Modal>
  );
}
